import {
    Collections,
    Delete,
    Edit,
    Image,
    ImageSearchRounded,
    InsertLink,
    LibraryAdd,
    YouTube,
  } from '@mui/icons-material';
  import {
    Box,
    Button,
    IconButton,
    Popover,
    TextField,
    Tooltip,
  } from '@mui/material';
  import { BubbleMenu, Editor } from '@tiptap/react';
  import { useState } from 'react';
import ResourcesView from '../../../components/resources';
  
  interface NoteMediaProps {
    editor: Editor;
    mode: number;
  }
  
  export default function NoteMedia({ editor, mode }: NoteMediaProps) {
    const [linkAnchorEl, setLinkAnchorEl] = useState<HTMLElement | null>(null);
    const [editLinkAnchorEl, setEditLinkAnchorEl] = useState<HTMLElement | null>(null);
    const [youtubeAnchorEl, setYoutubeAnchorEl] = useState<HTMLElement | null>(null);
    const [imageAnchorEl, setImageAnchorEl] = useState<HTMLElement | null>(null);
    
    const [resourcesOpen, setResourcesOpen] = useState(false);
    const [resourceAnchorEl, setResourceAnchorEl] = useState<null | HTMLElement>(null);
  
    const [link, setLink] = useState({ text: '', url: '' });

    const [youtubeUrl, setYoutubeUrl] = useState('');

    const [image, setImage] = useState({ src: '', alt: '' });
  
    const handleLinkChange = (key: string, value: string) => {
        setLink((prev) => ({ ...prev, [key]: value }));
    };
  
    const addLink = () => {
        if (link.url) {
            const { from, to } = editor.state.selection;
            const hasSelection = from !== to;

            if (hasSelection) {
                editor.chain().focus().setLink({ href: link.url }).run();
            } else {
                const textToInsert = link.text.trim() || link.url;
                editor.chain().focus().insertContent(`<a href="${link.url}">${textToInsert}</a>`).run();
            }

            editor.chain().focus().focus('end').run();
            setLinkAnchorEl(null);
            setLink({ text: '', url: '' });
        }
    };
    
    const updateLink = () => {
        if (link.url) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: link.url }).run();
            setEditLinkAnchorEl(null);
        }
    };
  
    const addYoutubeVideo = () => {
        if (youtubeUrl) {
            insertResizableTable();
            
            editor.commands.setYoutubeVideo({
                src: youtubeUrl,
                width: 480,
                height: 270,
            });
            setYoutubeUrl('');
            setYoutubeAnchorEl(null);
        }
    };
  
    const insertResizableTable = () => {
        editor.chain().focus().insertTable({ rows: 1, cols: 1, withHeaderRow: false }).run();
    };
      
    const addImage = (title: string, src: string) => {
        insertResizableTable();
        editor.chain().focus().setImage({ src: src, title: title }).run();
        setImageAnchorEl(null);
        setImage({ src: '', alt: '' })
        setResourceAnchorEl(null);
    };
    
  
    return (
        <>
            <Tooltip title="Link">
                <IconButton size="small" onClick={(e) => setLinkAnchorEl(e.currentTarget)}
                    sx={{
                        color: mode === 1 ? 'inherit' : 'white'
                    }}
                >
                    <InsertLink sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Library">
                <IconButton size="small" onClick={(e) => setResourceAnchorEl(e.currentTarget)}
                    sx={{
                        color: mode === 1 ? 'inherit' : 'white'
                    }}
                >
                    <Collections sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
    
            <Tooltip title="Image">
                <IconButton size="small" onClick={(e) => setImageAnchorEl(e.currentTarget)}
                    sx={{
                        color: mode === 1 ? 'inherit' : 'white'
                    }}
                >
                    <Image sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

                
            <Tooltip title="YouTube video">
                <IconButton size="small" onClick={(e) => setYoutubeAnchorEl(e.currentTarget)}
                    sx={{
                        color: mode === 1 ? 'inherit' : 'white'
                    }}    
                >
                    <YouTube sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
  
            <Popover
                open={Boolean(linkAnchorEl)}
                anchorEl={linkAnchorEl}
                onClose={() => setLinkAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, minWidth: 300 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Link Text"
                        value={link.text}
                        onChange={(e) => handleLinkChange('text', e.target.value)}
                        sx={{ mb: 1 }}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter URL"
                        value={link.url}
                        onChange={(e) => handleLinkChange('url', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addLink()}
                        sx={{ mb: 1 }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size="small" onClick={() => setLinkAnchorEl(null)}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={addLink}>Apply</Button>
                    </Box>
                </Box>
            </Popover>
  
            <Popover
                open={Boolean(editLinkAnchorEl)}
                anchorEl={editLinkAnchorEl}
                onClose={() => setEditLinkAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, minWidth: 300, borderRadius: 3 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Edit URL"
                        value={link.url}
                        onChange={(e) => handleLinkChange('url', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && updateLink()}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        <Button size="small" onClick={() => setEditLinkAnchorEl(null)}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={updateLink}>Update</Button>
                    </Box>
                </Box>
            </Popover>

            <Popover
                open={Boolean(youtubeAnchorEl)}
                anchorEl={youtubeAnchorEl}
                onClose={() => setYoutubeAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, minWidth: 300 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="YouTube URL"
                        value={youtubeUrl}
                        onChange={(e) => setYoutubeUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addYoutubeVideo()}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        <Button size="small" onClick={() => setYoutubeAnchorEl(null)}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={addYoutubeVideo}>Add</Button>
                    </Box>
                </Box>
            </Popover>
        
            <Popover
                open={Boolean(imageAnchorEl)}
                anchorEl={imageAnchorEl}
                onClose={() => setImageAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, minWidth: 300 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Image URL"
                        value={image.src}
                        onChange={(e) => setImage({ ...image, src: e.target.value })}
                        sx={{ mb: 1 }}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="Title"
                        value={image.alt}
                        onChange={(e) => setImage({ ...image, alt: e.target.value })}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        <Button size="small" onClick={() => setImageAnchorEl(null)}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={() => addImage(image.alt, image.src)}>Insert</Button>
                    </Box>
                </Box>
            </Popover>

            <ResourcesView 
                id="note-editor"
                resourceAnchorEl={resourceAnchorEl} 
                setResourceAnchorEl={setResourceAnchorEl} 
                onselect={(img: string) => {
                    setImage({...image, src: img});
                    addImage("nil", img);
                }}
            />
  

            <BubbleMenu
                editor={editor}
                shouldShow={({ editor }) => editor.isActive('link')}
                tippyOptions={{ duration: 100, placement: 'top' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        p: 1,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 3,
                        boxShadow: 3,
                        alignItems: 'center',
                        height: 20
                    }}
                >

                    <IconButton
                        size="small"
                        onClick={(e) => {
                            const previousUrl = editor.getAttributes('link').href;
                            setLink({ ...link, url: previousUrl });
                            setEditLinkAnchorEl(e.currentTarget);
                        }}
                        title="Edit Link"
                    >
                        <Edit />
                    </IconButton>
        
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => editor.chain().focus().unsetLink().run()}
                        title="Remove Link"
                    >
                        <Delete />
                    </IconButton>
                </Box>
            </BubbleMenu>
        </>
    );
  }
  